import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiHelper';

import { formatPrice } from '../utils/formatHelper';

import yurticiLogo from '../assets/images/yurtici-kargo-logo.png';
import arasLogo from '../assets/images/aras-cargo-logo.png';
import placeholderImage from '../assets/images/placeholder.jpg';
import LoadingSpinner from '../components/LoadingSpinner';
import ShipmentStatusWithInfo from '../components/ShipmentStatusWithInfo';
import { showSuccess, showError, showInfo, showServerError } from '../utils/toastHelper';

function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0
  });
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const statusMap = {
    'ACTIVE': { text: 'Aktif Siparişler', color: 'warning', statuses: ['ACTIVE'] },
    'SHIPPED': { text: 'Gönderimde', color: 'info', statuses: ['SHIPPED'] },
    'READY_TO_SHIP': { text: 'Kargolanmaya Hazır', color: 'primary', statuses: ['READY_TO_SHIP'] },
    'DELIVERED': { text: 'Teslim Edildi', color: 'success', statuses: ['DELIVERED'] },
    'CANCELLED': { text: 'İptal Edildi', color: 'secondary', statuses: ['CANCELLED'] },
    'REFUNDED': { text: 'İade Edildi', color: 'secondary', statuses: ['REFUNDED'] },
    'PAST': { text: 'Geçmiş Siparişler', color: 'info', statuses: ['PAST'] },
    'PENDING': { text: 'Beklemede', color: 'warning', statuses: ['PENDING'] },
    'PROCESSING': { text: 'İşleniyor', color: 'info', statuses: ['PROCESSING'] }
  };

  const getStatusInfo = (status) => {
    // Doğrudan eşleşme kontrolü
    if (statusMap[status]) {
      return statusMap[status];
    }

    // Statuses array'lerinde arama
    for (const [key, value] of Object.entries(statusMap)) {
      if (value.statuses.includes(status)) {
        return value;
      }
    }

    // Varsayılan değer
    return { text: status, color: 'secondary', statuses: [status] };
  };

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN' || user?.role === 'COMPANY_ADMIN') {
      navigate('/admin/orders', { replace: true });
    }
  }, [user?.role, navigate]);

  const fetchOrders = async (page = 1, status = 'all', dates = {}) => {
    try {
      setLoading(true);
      let queryParams = new URLSearchParams();

      // Add pagination params
      queryParams.append('page', page);
      queryParams.append('limit', pagination.limit);

      // Add status filter
      if (status !== 'all') {
        const statusValues = statusMap[status]?.statuses[0];
        if (statusValues) {
          queryParams.append('status', statusValues);
        }
      }

      // Add date filters if provided
      if (dates.startDate) {
        queryParams.append('startDate', dates.startDate);
      }
      if (dates.endDate) {
        queryParams.append('endDate', dates.endDate);
      }

      const response = await apiRequest(`/orders/?${queryParams.toString()}`);

      if (response.success) {
        setOrders(response.orders);
        setPagination(response.pagination);
      } else {
        showError(response.message || 'Siparişler yüklenirken bir hata oluştu');
      }
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        // API'ye ulaşılamıyor
        showServerError();
      }
      else {
        console.error('Failed to fetch orders:', error);
        showError(error.response.data.message || 'Siparişler yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchOrders(pagination.page, selectedStatus, dateRange);
    }
  }, [user?.id, selectedStatus, dateRange]);

  const handlePageChange = (newPage) => {
    fetchOrders(newPage, selectedStatus, dateRange);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    fetchOrders(1, status, dateRange);
  };

  const handleDateChange = (type, value) => {
    const newDateRange = { ...dateRange, [type]: value };
    setDateRange(newDateRange);
    fetchOrders(1, selectedStatus, newDateRange);
  };

  const clearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    fetchOrders(1, selectedStatus, { startDate: '', endDate: '' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };

    return date.toLocaleDateString('tr-TR', options);
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Siparişler yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page-container container py-4 py-md-5">
      {/* Header & Filters */}
      <div className="page-header d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-5 gap-4">
        <div>
          <h2 className="section-title mb-1">Siparişlerim</h2>
          <p className="text-muted section-subtitle mb-0">Tüm siparişlerinizi ve kargo süreçlerinizi buradan takip edebilirsiniz.</p>
        </div>

        <div className="filters-wrapper d-flex flex-column flex-md-row gap-3 align-items-md-center">
          {/* Date Range Filter */}
          <div className="date-filter-pill">
            <div className="d-flex align-items-center gap-2">
              <i className="fas fa-calendar-alt text-olive icon"></i>
              <input
                type="date"
                className="date-input"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
              />
              <span className="separator">-</span>
              <input
                type="date"
                className="date-input"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
              />
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  className="btn-clear-date"
                  onClick={clearDateFilter}
                  title="Temizle"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="status-tabs-container mb-5">
        <div className="status-tabs-scroll">
          <button
            className={`status-tab ${selectedStatus === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusChange('all')}
          >
            Tümü
          </button>
          <button
            className={`status-tab ${selectedStatus === 'ACTIVE' ? 'active' : ''}`}
            onClick={() => handleStatusChange('ACTIVE')}
          >
            Aktif
          </button>
          <button
            className={`status-tab ${selectedStatus === 'READY_TO_SHIP' ? 'active' : ''}`}
            onClick={() => handleStatusChange('READY_TO_SHIP')}
          >
            Kargolanmaya Hazır
          </button>
          <button
            className={`status-tab ${selectedStatus === 'SHIPPED' ? 'active' : ''}`}
            onClick={() => handleStatusChange('SHIPPED')}
          >
            Yolda
          </button>
          <button
            className={`status-tab ${selectedStatus === 'DELIVERED' ? 'active' : ''}`}
            onClick={() => handleStatusChange('DELIVERED')}
          >
            Teslim Edildi
          </button>
          <button
            className={`status-tab ${selectedStatus === 'CANCELLED' ? 'active' : ''}`}
            onClick={() => handleStatusChange('CANCELLED')}
          >
            İptal Edildi
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="empty-state text-center py-5">
          <div className="empty-icon-wrapper mb-4">
            <i className="fas fa-box-open fa-3x"></i>
          </div>
          <h4 className="empty-title mb-2">Henüz siparişiniz bulunmuyor</h4>
          <p className="empty-subtitle text-muted">Abonelikleriniz üzerinden yeni bir sipariş oluşturduğunuzda burada görünecektir.</p>
        </div>
      ) : (
        <div className="orders-grid">
          {orders.map((order) => (
            <div
              key={order.id}
              className={`modern-order-card ${expandedOrders.has(order.id) ? 'expanded' : ''}`}
              data-order-status={order.status}
              onClick={() => toggleOrderDetails(order.id)}
            >
              <div className="card-indicator"></div>
              
              <div className="card-main-content">
                {/* Header Row */}
                <div className="order-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
                  <div className="order-title-group">
                    <span className="order-id">#{order.orderNumber}</span>
                    <span className="order-date"><i className="far fa-calendar-alt me-1"></i> {formatDate(order.createdAt)}</span>
                  </div>
                  <div className="order-status-wrapper">
                    {order.shipment ? (
                      <ShipmentStatusWithInfo status={order.shipment.status} className="premium-badge" />
                    ) : (
                      <span className={`premium-badge badge-color-${getStatusInfo(order.status).color}`}>
                        {getStatusInfo(order.status).text}
                      </span>
                    )}
                  </div>
                </div>

                <hr className="divider" />

                {/* Body Row */}
                <div className="order-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4">
                  
                  {/* Product Images Overlap */}
                  <div className="product-summary d-flex align-items-center gap-3">
                    <div className="product-images-stack">
                      {order.products.slice(0, 3).map((product, idx) => (
                        <div key={idx} className="stack-img-wrapper" style={{ zIndex: 3 - idx }}>
                          <img src={product.mainImage || placeholderImage} alt={product.name} />
                        </div>
                      ))}
                      {order.products.length > 3 && (
                        <div className="stack-more">
                          +{order.products.length - 3}
                        </div>
                      )}
                    </div>
                    <div className="product-text">
                      <span className="count-text fw-semibold">{order.products.length} Ürün</span>
                      {order.deliveryCount > 1 && <span className="delivery-text text-muted d-block small">{order.deliveryCount} Teslimat</span>}
                    </div>
                  </div>

                  {/* Amount & Actions */}
                  <div className="action-summary d-flex align-items-center justify-content-between justify-content-md-end gap-4 w-100-mobile">
                    <div className="amount-wrapper text-start text-md-end">
                      <span className="amount-label d-block text-muted small">Toplam Tutar</span>
                      <span className="amount-value text-olive fw-bold fs-5">{formatPrice(order.totalAmount)}</span>
                    </div>
                    
                    <button
                      className="btn-expand-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOrderDetails(order.id);
                      }}
                      aria-label="Detayları Gör"
                    >
                      <i className={`fas fa-chevron-${expandedOrders.has(order.id) ? 'up' : 'down'}`}></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Expandable Details */}
              <div className={`order-expanded-details ${expandedOrders.has(order.id) ? 'show' : ''}`}>
                <div className="details-inner p-4 p-md-5">
                  <div className="row g-4 mb-4">
                    {/* Delivery & Tracking */}
                    <div className="col-lg-6">
                      <div className="detail-panel h-100">
                        <div className="panel-header">
                          <div className="icon-box bg-olive-light text-olive"><i className="fas fa-truck-fast"></i></div>
                          <h6 className="mb-0 fw-semibold">Teslimat Bilgileri</h6>
                        </div>
                        <div className="panel-body">
                          {order.shipment ? (
                            <div className="carrier-box mb-3 p-3 rounded-3 bg-light">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  {order.shipment.carrier?.code === 'AK' && <img src={arasLogo} alt="Aras" height="24" className="rounded" />}
                                  {order.shipment.carrier?.code === 'YK' && <img src={yurticiLogo} alt="Yurtiçi" height="24" className="rounded" />}
                                  <span className="fw-medium text-dark">{order.shipment.carrier?.name || 'Kargo'}</span>
                                </div>
                                <ShipmentStatusWithInfo status={order.shipment.status} />
                              </div>
                              
                              {order.shipment.trackingNumber && (
                                <div className="tracking-section d-flex align-items-center justify-content-between py-2 border-top border-bottom mb-3">
                                  <div>
                                    <span className="d-block text-muted small mb-1">Takip Numarası</span>
                                    <span className="fw-mono fw-semibold text-dark">{order.shipment.trackingNumber}</span>
                                  </div>
                                  <button
                                    className="btn btn-sm btn-icon btn-light rounded-circle shadow-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(order.shipment.trackingNumber);
                                    }}
                                    title="Kopyala"
                                  >
                                    <i className="far fa-copy text-muted"></i>
                                  </button>
                                </div>
                              )}
                              
                              {(() => {
                                const code = order.shipment.carrier?.code;
                                const tn = order.shipment.trackingNumber;
                                let trackingUrl = null;
                                if (code === 'AK') trackingUrl = `https://www.araskargo.com.tr/tr/kargom-nerede?tracking=${tn}`;
                                else if (code === 'YK') trackingUrl = `https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=${tn}`;
                                if (!trackingUrl || !tn) return null;
                                return (
                                  <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-olive btn-sm w-100 mt-1 d-flex justify-content-center align-items-center gap-2 fw-medium rounded-pill">
                                    <i className="fas fa-location-arrow"></i> Kargo Takibi
                                  </a>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="mb-4">
                              <span className="text-muted d-block small mb-1">Planlanan Teslimat</span>
                              <span className="fw-medium text-dark"><i className="far fa-calendar-check me-2 text-olive"></i>{formatDate(order.plannedDeliveryDate)}</span>
                            </div>
                          )}

                          <div className="address-box border-top pt-3">
                            <span className="text-muted small d-block mb-1">Gönderim Adresi</span>
                            <p className="fw-medium text-dark mb-1">{order.shippingAddress.fullName}</p>
                            <p className="text-muted small mb-1 lh-base">{order.shippingAddress.address}</p>
                            <p className="text-muted small mb-0">{order.shippingAddress.district}, {order.shippingAddress.city}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Meta Info */}
                    <div className="col-lg-6">
                      <div className="detail-panel h-100">
                        <div className="panel-header">
                          <div className="icon-box bg-orange-light text-orange"><i className="fas fa-receipt"></i></div>
                          <h6 className="mb-0 fw-semibold">Sipariş Özeti</h6>
                        </div>
                        <div className="panel-body">
                          <div className="summary-list">
                            <div className="summary-item">
                              <span className="text-muted">Sipariş No</span>
                              <span className="fw-medium">{order.orderNumber}</span>
                            </div>
                            <div className="summary-item">
                              <span className="text-muted">Sipariş Tarihi</span>
                              <span className="fw-medium">{formatDate(order.createdAt)}</span>
                            </div>
                            {order.actualDeliveryDate && (
                              <div className="summary-item">
                                <span className="text-muted">Teslim Edilme</span>
                                <span className="fw-medium">{formatDate(order.actualDeliveryDate)}</span>
                              </div>
                            )}
                            <div className="summary-item">
                              <span className="text-muted">İletişim Numarası</span>
                              <span className="fw-medium">{order.shippingAddress.phone}</span>
                            </div>
                          </div>
                          
                          <div className="total-box mt-4 p-3 bg-light rounded-3 d-flex justify-content-between align-items-center">
                            <span className="fw-semibold text-dark">Genel Toplam</span>
                            <span className="fs-5 fw-bold text-olive">{formatPrice(order.totalAmount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products Table/List */}
                  <div className="products-panel rounded-4">
                    <div className="p-3 p-md-4 border-bottom">
                      <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-olive">
                        <i className="fas fa-shopping-bag"></i> Satın Alınan Ürünler
                      </h6>
                    </div>
                    
                    {/* Desktop Table View */}
                    <div className="table-responsive d-none d-md-block">
                      <table className="table modern-table mb-0">
                        <thead>
                          <tr>
                            <th className="ps-4 border-0">Ürün</th>
                            <th className="text-center border-0">Miktar</th>
                            <th className="text-end border-0">Birim Fiyat</th>
                            <th className="text-end pe-4 border-0">Toplam</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.products.map((product, idx) => (
                            <tr key={idx}>
                              <td className="ps-4">
                                <div className="d-flex align-items-center gap-3 py-2">
                                  <div className="custom-thumb">
                                    <img src={product.mainImage || placeholderImage} alt={product.name} />
                                  </div>
                                  <span className="fw-medium text-dark">{product.name}</span>
                                </div>
                              </td>
                              <td className="text-center align-middle">
                                <span className="badge bg-light text-dark px-2 py-1 rounded-pill border">x{product.quantity}</span>
                              </td>
                              <td className="text-end align-middle fw-medium text-muted">{formatPrice(product.price)}</td>
                              <td className="text-end align-middle fw-bold text-dark pe-4">{formatPrice(product.quantity * product.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile List View */}
                    <div className="mobile-product-list d-md-none p-3">
                      {order.products.map((product, idx) => (
                        <div key={idx} className="mobile-product-item">
                          <div className="custom-thumb">
                            <img src={product.mainImage || placeholderImage} alt={product.name} />
                          </div>
                          <div className="item-info">
                            <div className="fw-medium text-dark mb-1 line-clamp-2 lh-sm">{product.name}</div>
                            <div className="d-flex justify-content-between align-items-end mt-auto">
                              <div>
                                <span className="text-muted small d-block">{formatPrice(product.price)} / adet</span>
                                <span className="badge bg-light text-dark px-2 rounded-pill border mt-1">x{product.quantity}</span>
                              </div>
                              <span className="fw-bold text-dark">{formatPrice(product.quantity * product.price)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modern Pagination */}
      {pagination.total_pages > 1 && (
        <div className="pagination-wrapper mt-5 mb-4">
          <div className="d-flex justify-content-center align-items-center gap-2">
            <button
              className="btn-page nav-btn"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <i className="fas fa-chevron-left me-1"></i> Geri
            </button>

            <div className="page-numbers d-none d-sm-flex gap-1">
              {[...Array(pagination.total_pages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first, last, current, and adjacent
                if (
                  pageNum === 1 || 
                  pageNum === pagination.total_pages || 
                  (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={`btn-page num-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === pagination.page - 2 ||
                  pageNum === pagination.page + 2
                ) {
                  return <span key={pageNum} className="text-muted align-self-end px-1">...</span>;
                }
                return null;
              })}
            </div>
            
            {/* Mobile counter */}
            <div className="d-sm-none page-counter text-muted small fw-medium px-3 py-2 bg-light rounded-pill">
              {pagination.page} / {pagination.total_pages}
            </div>

            <button
              className="btn-page nav-btn"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages}
            >
              İleri <i className="fas fa-chevron-right ms-1"></i>
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          .orders-page-container {
            --kito-olive: #6fa063;
            --kito-olive-dark: #5a8550;
            --kito-olive-light: rgba(111, 160, 99, 0.1);
            --kito-orange: #f29c38;
            --kito-orange-light: rgba(242, 156, 56, 0.1);
            --surface: #ffffff;
            --surface-hover: #f9fafb;
            --border: #eaecf0;
            --text-main: #101828;
            --text-secondary: #475467;
            --shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.05);
            --shadow-md: 0 4px 6px -1px rgba(16, 24, 40, 0.1), 0 2px 4px -2px rgba(16, 24, 40, 0.06);
            --shadow-lg: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
            --radius-md: 12px;
            --radius-lg: 16px;
            --radius-xl: 20px;
            --transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            background-color: #fcfcfd;
          }

          .orders-page-container {
            max-width: 1200px;
          }

          .section-title {
            font-size: 2rem;
            font-weight: 700;
            color: var(--text-main);
            letter-spacing: -0.02em;
          }

          .section-subtitle {
            font-size: 1.05rem;
          }

          /* Date Filter Pill */
          .date-filter-pill {
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: 100px;
            padding: 0.5rem 1rem;
            box-shadow: var(--shadow-sm);
            transition: var(--transition);
          }
          .date-filter-pill:focus-within {
            border-color: var(--kito-olive);
            box-shadow: 0 0 0 4px var(--kito-olive-light);
          }
          .date-input {
            border: none;
            background: transparent;
            color: var(--text-main);
            font-size: 0.9rem;
            font-weight: 500;
            outline: none;
            width: 120px;
            cursor: pointer;
            font-family: inherit;
          }
          .date-input::-webkit-calendar-picker-indicator {
            cursor: pointer;
            opacity: 0.6;
            transition: opacity 0.2s;
          }
          .date-input::-webkit-calendar-picker-indicator:hover {
            opacity: 1;
          }
          .btn-clear-date {
            border: none;
            background: #f1f5f9;
            color: var(--text-secondary);
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8rem;
            transition: var(--transition);
          }
          .btn-clear-date:hover {
            background: #e2e8f0;
            color: #ef4444;
          }

          /* Status Tabs */
          .status-tabs-container {
            position: relative;
            margin: 0 -1rem; /* Full bleed on mobile */
            padding: 0 1rem;
          }
          @media (min-width: 768px) {
            .status-tabs-container { margin: 0; padding: 0; }
          }
          .status-tabs-scroll {
            display: flex;
            gap: 0.5rem;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
            padding-bottom: 0.25rem;
            mask-image: linear-gradient(to right, black 90%, transparent 100%);
            -webkit-mask-image: linear-gradient(to right, black 90%, transparent 100%);
          }
          .status-tabs-scroll::-webkit-scrollbar { display: none; }
          .status-tab {
            background: var(--surface);
            border: 1px solid var(--border);
            padding: 0.625rem 1.25rem;
            border-radius: 100px;
            font-weight: 600;
            font-size: 0.9rem;
            color: var(--text-secondary);
            white-space: nowrap;
            transition: var(--transition);
            box-shadow: var(--shadow-sm);
          }
          .status-tab:hover {
            background: var(--surface-hover);
            color: var(--text-main);
            transform: translateY(-1px);
          }
          .status-tab.active {
            background: var(--text-main);
            color: white;
            border-color: var(--text-main);
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }

          /* Empty State */
          .empty-icon-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 96px;
            height: 96px;
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: 50%;
            color: var(--border);
            box-shadow: var(--shadow-sm);
            margin: 0 auto;
          }

          /* Modern Order Card */
          .orders-grid {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }
          
          .modern-order-card {
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: var(--radius-lg);
            position: relative;
            transition: var(--transition);
            cursor: pointer;
            box-shadow: var(--shadow-sm);
            overflow: hidden;
            z-index: 1;
            text-align: left;
          }
          
          .modern-order-card:hover {
            box-shadow: var(--shadow-lg);
            border-color: rgba(111, 160, 99, 0.4);
            transform: translateY(-2px);
          }
          
          .modern-order-card.expanded {
            border-color: var(--kito-olive);
            box-shadow: var(--shadow-md);
            transform: none;
          }

          /* Indicator Bar */
          .card-indicator {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background-color: var(--border);
            transition: var(--transition);
          }
          .modern-order-card[data-order-status="DELIVERED"] .card-indicator { background-color: #10b981; }
          .modern-order-card[data-order-status="SHIPPED"] .card-indicator { background-color: #3b82f6; }
          .modern-order-card[data-order-status="READY_TO_SHIP"] .card-indicator { background-color: #6366f1; }
          .modern-order-card[data-order-status="CANCELLED"] .card-indicator,
          .modern-order-card[data-order-status="REFUNDED"] .card-indicator { background-color: #f43f5e; }
          .modern-order-card[data-order-status="ACTIVE"] .card-indicator,
          .modern-order-card[data-order-status="PENDING"] .card-indicator,
          .modern-order-card[data-order-status="PROCESSING"] .card-indicator { background-color: var(--kito-orange); }

          .card-main-content {
            padding: 1.5rem;
            padding-left: 1.75rem;
          }

          /* Header */
          .order-id {
            font-size: 1.2rem;
            font-weight: 700;
            color: var(--text-main);
            letter-spacing: -0.01em;
          }
          .order-date {
            font-size: 0.9rem;
            color: var(--text-secondary);
            font-weight: 500;
            background: #f1f5f9;
            padding: 0.25rem 0.6rem;
            border-radius: 6px;
          }
          hr.divider {
            margin: 1.25rem 0;
            color: var(--border);
            opacity: 1;
            border-top: 1px solid var(--border);
          }

          /* Badges */
          .premium-badge {
            display: inline-flex;
            align-items: center;
            padding: 0.4rem 0.8rem;
            border-radius: 100px;
            font-weight: 600;
            font-size: 0.85rem;
            letter-spacing: 0.02em;
          }
          .badge-color-success { background: #d1fae5; color: #059669; }
          .badge-color-info { background: #e0f2fe; color: #0284c7; }
          .badge-color-primary { background: #e0e7ff; color: #4f46e5; }
          .badge-color-warning { background: #fef3c7; color: #d97706; }
          .badge-color-secondary { background: #f1f5f9; color: #475467; }

          /* Product Stack */
          .product-images-stack {
            display: flex;
            align-items: center;
          }
          .stack-img-wrapper {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 2px solid var(--surface);
            background: var(--border);
            overflow: hidden;
            margin-left: -12px;
            box-shadow: var(--shadow-sm);
          }
          .stack-img-wrapper:first-child { margin-left: 0; }
          .stack-img-wrapper img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .stack-more {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 2px solid var(--surface);
            background: var(--text-main);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 0.85rem;
            margin-left: -12px;
            z-index: 0;
          }
          .product-text {
            line-height: 1.2;
          }

          /* Action Btn */
          .btn-expand-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: var(--surface-hover);
            border: 1px solid var(--border);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--text-secondary);
            transition: var(--transition);
            flex-shrink: 0;
          }
          .modern-order-card:hover .btn-expand-icon {
            background: var(--kito-olive-light);
            color: var(--kito-olive);
            border-color: var(--kito-olive);
          }
          .modern-order-card.expanded .btn-expand-icon {
            background: var(--kito-olive);
            color: white;
            border-color: var(--kito-olive);
          }

          /* Expanded Area */
          .order-expanded-details {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            background: #fcfcfd;
            border-top: 1px solid var(--border);
          }
          .order-expanded-details.show {
            max-height: 2000px;
          }

          /* Detail Panels */
          .detail-panel {
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: var(--radius-lg);
            padding: 1.5rem;
            box-shadow: var(--shadow-sm);
          }
          .panel-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.25rem;
          }
          .icon-box {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.1rem;
          }
          .bg-olive-light { background: var(--kito-olive-light); }
          .text-olive { color: var(--kito-olive) !important; }
          .bg-orange-light { background: var(--kito-orange-light); }
          .text-orange { color: var(--kito-orange) !important; }

          .summary-list .summary-item {
            display: flex;
            justify-content: space-between;
            padding: 0.6rem 0;
            border-bottom: 1px dashed var(--border);
          }
          .summary-list .summary-item:last-child {
            border-bottom: none;
          }

          /* Products Table Refinement */
          .products-panel {
            background: var(--surface);
            border: 1px solid var(--border);
            box-shadow: var(--shadow-sm);
            overflow: hidden;
          }
          .modern-table th {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--text-secondary);
            font-weight: 600;
            background: #f8fafc;
            padding-top: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--border);
          }
          .custom-thumb {
            width: 56px;
            height: 56px;
            border-radius: 10px;
            overflow: hidden;
            border: 1px solid var(--border);
            flex-shrink: 0;
            background: var(--surface);
          }
          .custom-thumb img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          /* Mobile Product List */
          .mobile-product-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .mobile-product-item {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            background: #f8fafc;
            border-radius: var(--radius-md);
            border: 1px solid var(--border);
          }
          .item-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-width: 0;
          }
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          /* Pagination Custom */
          .nav-btn, .num-btn {
            background: var(--surface);
            border: 1px solid var(--border);
            color: var(--text-secondary);
            font-weight: 600;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            transition: var(--transition);
          }
          .nav-btn {
            padding: 0 1rem;
            border-radius: 100px;
          }
          .num-btn {
            width: 40px;
          }
          .nav-btn:hover:not(:disabled), .num-btn:hover:not(.active) {
            background: var(--surface-hover);
            color: var(--text-main);
          }
          .num-btn.active {
            background: var(--kito-olive);
            color: white;
            border-color: var(--kito-olive);
          }
          .nav-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          /* Responsive Tweaks */
          @media (max-width: 575.98px) {
            .card-main-content {
              padding: 1.25rem;
              padding-left: 1.5rem;
            }
            .order-title-group {
              display: flex;
              align-items: center;
              gap: 0.75rem;
            }
            .w-100-mobile {
              width: 100%;
            }
            .details-inner {
              padding: 1.25rem;
            }
            .product-summary {
              margin-bottom: 0.5rem;
            }
            .order-expanded-details.show {
              max-height: 3000px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Orders;
